import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assessment as ReportIcon,
  Security as SecurityIcon,
  DevicesOutlined as DevicesIcon,
  Dashboard as DashboardIcon,
  BusinessCenter as BusinessIcon,
  BarChart as ChartIcon,
  Speed as SpeedIcon,
  Work as BriefcaseIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from './Logo';
import Header from './Header';
import Footer from './Footer';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import DownloadApp from './DownloadApp';

const features = [
  {
    icon: <PeopleIcon sx={{ color: 'primary.main', fontSize: 40 }} />,
    title: 'Employee Management',
    description: 'Keep employee records, W-4 elections, pay type, and work state in one place.',
  },
  {
    icon: <MoneyIcon sx={{ color: 'primary.main', fontSize: 40 }} />,
    title: 'Payroll Calculations',
    description: 'Estimate federal withholding, FICA, and overtime. You pay employees and file taxes yourself.',
  },
  {
    icon: <ReportIcon sx={{ color: 'primary.main', fontSize: 40 }} />,
    title: 'Pay Statements & Reports',
    description: 'Download recorded pay statements and run history from your payroll records.',
  },
  {
    icon: <SecurityIcon sx={{ color: 'primary.main', fontSize: 40 }} />,
    title: 'Access Controls',
    description: 'Role-based permissions, HTTPS, and Firestore rules. Last-4 SSN and bank last-4 only.',
  },
  {
    icon: <DevicesIcon sx={{ color: 'primary.main', fontSize: 40 }} />,
    title: 'Works in the Browser',
    description: 'Use the web app from a desktop or phone. Companion shells load the same product.',
  },
];

const benefits = [
  'Calculate payroll with published IRS and FLSA formulas',
  'Record pay runs without pretending money moved',
  'Keep W-4 elections and last-4 identifiers on file',
  'Give employees access to recorded pay statements',
  'HTTPS and role-based access in Firebase',
];

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { mode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = mode === 'dark';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      {/* Global Header */}
      <Header />

      <Box sx={{ flexGrow: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: { xs: 4, sm: 6, md: 8 },
            px: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
            <Grid container spacing={isMobile ? 2 : 4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography 
                  variant={isSmallMobile ? "h3" : "h2"} 
                  fontWeight="bold" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                    mb: { xs: 1.5, sm: 2 }
                  }}
                >
                  Features That Simplify Your Payroll
                </Typography>
                <Typography 
                  variant={isSmallMobile ? "body1" : "h5"} 
                  sx={{ 
                    mb: { xs: 2, sm: 3 },
                    opacity: 0.9,
                    fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.5rem' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
                  }}
                >
                  Superior Payroll combines powerful functionality with ease of use to make payroll management effortless for businesses of all sizes.
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 2 },
                  mt: { xs: 1, sm: 2 }
                }}>
                  {currentUser ? (
                    <>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/dashboard')}
                        sx={{
                          bgcolor: 'common.white',
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: 'grey.100',
                          },
                          px: { xs: 2, sm: 3, md: 4 },
                          py: { xs: 1, sm: 1.5 },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Go to Dashboard
                      </Button>
                      
                      {isMobile && (
                        <DownloadApp
                          variant="button" 
                          showText={true}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/register')}
                        sx={{
                          bgcolor: theme.palette.secondary.main,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                          },
                          px: { xs: 2, sm: 3, md: 4 },
                          py: { xs: 1, sm: 1.5 },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                          width: { xs: '100%', sm: 'auto' },
                          mb: { xs: 1.5, sm: 0 }
                        }}
                      >
                        Create an account
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/login')}
                        sx={{
                          borderColor: 'common.white',
                          color: 'common.white',
                          '&:hover': {
                            borderColor: 'grey.300',
                            bgcolor: 'rgba(255, 255, 255, 0.08)',
                          },
                          px: { xs: 2, sm: 3, md: 4 },
                          py: { xs: 1, sm: 1.5 },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Log In
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Paper
                  elevation={3}
                  sx={{
                    width: '100%',
                    maxWidth: 600,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 3,
                    transform: 'perspective(1000px) rotateY(-10deg) rotateX(5deg)',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg) scale(1.02)',
                    },
                    bgcolor: 'background.paper',
                    p: 3,
                  }}
                >
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <DashboardIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">Superior Payroll Dashboard</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Total Employees</Typography>
                          <Typography variant="h4">156</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Monthly Payroll</Typography>
                          <Typography variant="h4">$284,500</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Recent Activity</Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                              Payroll processed for June 2024
                            </Typography>
                            <Typography variant="body2" sx={{ py: 0.5 }}>
                              New employee: Jane Smith added
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Container sx={{ 
          pt: { xs: 4, sm: 5, md: 8 }, 
          pb: { xs: 4, sm: 5, md: 8 },
          px: { xs: 2, sm: 3, md: 4 }
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: { xs: 3, sm: 4, md: 5 },
              textAlign: 'center', 
              pt: { xs: 1, sm: 1.5, md: 2 },
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            Powerful Features for Modern Businesses
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ 
              mb: { xs: 4, sm: 5 },
              mx: 'auto', 
              maxWidth: 800,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            Our payroll calculator and record keeper is designed to save you time. You remain responsible for paying employees, depositing taxes, and staying compliant.
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6,
                    },
                    borderRadius: 2
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      {feature.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      align="center"
                      sx={{ 
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        mb: 1
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      align="center"
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '0.95rem' }
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Benefits Section */}
        <Box
          sx={{
            bgcolor: isDark ? 'background.paper' : 'grey.100',
            py: { xs: 5, sm: 6, md: 8 },
          }}
        >
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 2
                  }}
                >
                  Why Superior Payroll?
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    mb: 2
                  }}
                >
                  Our modern payroll system simplifies complex payroll tasks, automates calculations, and ensures compliance with tax regulations.
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1rem' }
                  }}
                >
                  We understand the challenges businesses face with payroll management, so we've built a solution that makes it easy, accurate, and efficient.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 1.25 }}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit} 
                        primaryTypographyProps={{ 
                          fontSize: { xs: '0.95rem', sm: '1rem' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{ 
                    mt: 3,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Create an account
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Trusted by Businesses Section */}
        <Box sx={{ py: { xs: 5, sm: 6, md: 8 }, display: 'flex' }}>
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} sx={{ mb: { xs: 3, md: 0 } }}>
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    bgcolor: isDark ? 'primary.dark' : 'primary.light',
                    color: 'primary.contrastText',
                    borderRadius: 2
                  }}
                >
                  <BriefcaseIcon sx={{ fontSize: { xs: 60, sm: 80 }, mb: 2 }} />
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    fontWeight="bold"
                    sx={{ 
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                      mb: 1.5
                    }}
                  >
                    Payroll software, not a bureau
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      mb: 2
                    }}
                  >
                    Record payroll calculations and pay lists in one place.
                    You pay employees and file taxes yourself.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ 
                      mt: 2, 
                      bgcolor: 'common.white', 
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    onClick={() => navigate('/register')}
                  >
                    Create an account
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2
                  }}
                >
                  <Box>
                    <Typography 
                      variant="h4" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ 
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        mb: 1.5
                      }}
                    >
                      Secure & Compliant
                    </Typography>
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        mb: 2
                      }}
                    >
                      Data is sent over HTTPS and stored in Google Firebase with Google Cloud encryption at rest. Last-4 SSN and bank last-4 only — we do not store full account numbers.
                    </Typography>
                    <Typography 
                      variant="body1"
                      sx={{ 
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        mb: 1
                      }}
                    >
                      You remain responsible for paying wages and filing taxes. We help you:
                    </Typography>
                    <List>
                      <ListItem disableGutters sx={{ py: 0.75 }}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="HTTPS in transit, Google Cloud encryption at rest" 
                          primaryTypographyProps={{ 
                            fontSize: { xs: '0.9rem', sm: '0.95rem' }
                          }}
                        />
                      </ListItem>
                      <ListItem disableGutters sx={{ py: 0.75 }}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Last-4 SSN and bank last-4 only" 
                          primaryTypographyProps={{ 
                            fontSize: { xs: '0.9rem', sm: '0.95rem' }
                          }}
                        />
                      </ListItem>
                      <ListItem disableGutters sx={{ py: 0.75 }}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Withholding formulas you can review; you remain responsible for filing" 
                          primaryTypographyProps={{ 
                            fontSize: { xs: '0.9rem', sm: '0.95rem' }
                          }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            bgcolor: 'primary.dark',
            color: 'common.white',
            py: { xs: 4, sm: 5, md: 8 },
            px: { xs: 2, sm: 3, md: 4 },
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                mb: { xs: 1.5, sm: 2, md: 2.5 }
              }}
            >
              Ready to simplify your payroll process?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: { xs: 2, sm: 3, md: 4 },
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                px: { xs: 1, sm: 2, md: 0 }
              }}
            >
              Create an account to calculate pay, record runs, and print payment instructions.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: { xs: 1.5, sm: 2 },
            }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'common.white',
                  color: 'primary.dark',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  mb: { xs: 1.5, sm: 0 }
                }}
              >
                Sign Up Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: 'common.white',
                  color: 'common.white',
                  '&:hover': {
                    borderColor: 'grey.300',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Log In
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Mobile app download section - improved spacing */}
        {isMobile && currentUser && (
          <Box sx={{ 
            mt: 1, 
            mb: 2, 
            pb: 1, 
            px: { xs: 2, sm: 3 },
            display: 'flex',
            justifyContent: 'center'
          }}>
            <DownloadApp
              variant="button" 
              showText={true}
            />
          </Box>
        )}
      </Box>

      {/* Global Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage; 